import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { signIn, error: authError } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email || !password) return;

    setIsSubmitting(true);
    
    try {
      const { data, error } = await signIn(email, password);
      
      if (!error && data?.user) {
        // Check user role from profile or metadata
        const userRole = data?.user?.user_metadata?.role || 'partner';
        
        // Navigate based on role
        switch (userRole) {
          case 'admin': navigate('/admin-dashboard');
            break;
          case 'partner': case'agent': case'booker': case'venue':
          default:
            navigate('/partner-dashboard');
            break;
        }
      }
    } catch (error) {
      // Error is handled by AuthContext
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-accent to-accent/70 rounded-xl flex items-center justify-center">
            <Icon name="Music" size={24} className="text-white" />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-foreground mb-2">Bienvenue sur Artis.io</h1>
        <p className="text-muted-foreground text-lg">
          Connectez-vous à votre espace partenaire
        </p>
      </div>
      {/* Login Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {authError && (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-destructive" />
              <p className="text-destructive text-sm">{authError}</p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
              Adresse email
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e?.target?.value)}
              placeholder="votre.email@exemple.com"
              required
              className="w-full"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
              Mot de passe
            </label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e?.target?.value)}
                placeholder="••••••••"
                required
                className="w-full pr-12"
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                disabled={isSubmitting}
              >
                <Icon 
                  name={showPassword ? 'EyeOff' : 'Eye'} 
                  size={18} 
                />
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="remember-me"
              type="checkbox"
              className="h-4 w-4 text-accent border-border rounded focus:ring-accent focus:ring-2"
              disabled={isSubmitting}
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm text-muted-foreground">
              Se souvenir de moi
            </label>
          </div>

          <button
            type="button"
            className="text-sm text-accent hover:text-accent/80 transition-colors"
            disabled={isSubmitting}
          >
            Mot de passe oublié ?
          </button>
        </div>

        <Button
          type="submit"
          className="w-full"
          loading={isSubmitting}
          disabled={!email || !password}
        >
          Se connecter
        </Button>
      </form>
      {/* Sign Up Link */}
      <div className="mt-8 text-center">
        <p className="text-muted-foreground">
          Pas encore de compte ?{' '}
          <button
            onClick={() => navigate('/partner-registration')}
            className="text-accent hover:text-accent/80 font-medium transition-colors"
            disabled={isSubmitting}
          >
            Devenir partenaire
          </button>
        </p>
      </div>
      {/* Demo Credentials */}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border">
        <p className="text-sm text-muted-foreground mb-2 font-medium">Comptes de démonstration :</p>
        <div className="space-y-1 text-xs text-muted-foreground">
          <p><strong>Admin:</strong> admin@artis.io / password123</p>
          <p><strong>Partenaire:</strong> partner@artis.io / password123</p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;